import React, {
    forwardRef, useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';
import mergeRefs from 'merge-refs';

import {SlideArrows, calculateItemsEdges, getScrollMetrics, useScrollNavigation} from '@pb/kit-component-scroll';
import {ArrowNavigationContext, useArrowNavigation} from '@pb/kit-hook-use-arrow-navigation';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';
import {uniqId} from '@pb/kit-util-uniq-id';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {useScrollableTabs} from './hooks/use-scrollable-tabs';
import {TabsContext} from './tabs-context';

import styles from './tabs.component.module.styl';

import type {ComponentProps, ReactNode} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

export const HORIZONTAL_DIRECTIONS = ['LEFT', 'RIGHT'] as const;
export const VERTICAL_DIRECTIONS = ['TOP', 'BOTTOM'] as const;

const OFFSET_TOLERANCE = 3;

type NativeULProps = Omit<ComponentProps<'ul'>, 'onChange'>;
export type DirectionType = (typeof HORIZONTAL_DIRECTIONS[number] | typeof VERTICAL_DIRECTIONS[number]);

export interface TabsProps extends NativeULProps {
    /**
     * When `true`, shows scroll-arrow buttons when the tab list overflows the
     * container. Defaults to `true` for horizontal tabs and `false` for vertical.
     */
    enableScroll?: boolean,
    /**
     * When `true` (default), enables keyboard arrow navigation between tabs.
     * Users can move focus with `ArrowLeft` / `ArrowRight` (horizontal) or
     * `ArrowUp` / `ArrowDown` (vertical).
     */
    enableArrowNavigation?: boolean,
    /**
     * Enable text truncation for tab captions.
     * When `true` (default), captions are truncated to a single line with an
     * ellipsis. When `false`, captions can wrap and the tab height grows with content.
     * @default true
     */
    ellipsis?: boolean,
    /**
     * `<Tab>` nodes (or `<SidebarTab>`, `<LinkTab>`) rendered inside the tab list.
     */
    children?: ReactNode,
    /**
     * Called when the user clicks a scroll arrow. Receives the direction string:
     * `"LEFT"` / `"RIGHT"` for horizontal, `"TOP"` / `"BOTTOM"` for vertical.
     */
    onArrowClick?: (direction: DirectionType) => void,
    /**
     * Called when the selected tab changes. Receives the `value` string of the
     * newly selected `<Tab>`. Use for controlled tab selection.
     */
    onChange?: (value: string) => void,
    /**
     * Layout direction of the tab list. `"horizontal"` (default) arranges tabs
     * in a row; `"vertical"` arranges them in a column (sidebar style).
     */
    orientation?: 'horizontal' | 'vertical',
    /**
     * Ref forwarded to the root `<div>` wrapper element that contains the scroll
     * container and slide-arrow buttons.
     */
    rootRef?: React.Ref<HTMLDivElement>,
    /**
     * Native props forwarded to the root `<div>` wrapper (e.g. `className`,
     * `style`, `data-testid`).
     */
    rootProps?: ComponentProps<'div'>,
    /**
     * The `value` of the currently selected tab. When provided the component
     * is controlled; omit to let `<Tabs>` manage selection internally.
     */
    value?: string,
    /**
     * The `id` of the `<TabPanel>` element associated with this tab list. Used to
     * set `aria-controls` on the active `<Tab>` for accessibility.
     */
    tabPanelId?: string,
    /**
     * Custom `id` for the `role="tablist"` `<ul>` element. Auto-generated when
     * omitted. Exposed so the tabs can be linked from an external `aria-labelledby`.
     */
    tabsId?: string,
}

/**
 * The **Tabs** component is a container for managing multiple **Tab** elements, providing navigation, keyboard accessibility, and optional scrolling support.
 *
 * **Features**
 *
 * - Supports horizontal and vertical orientation
 * - Provides **keyboard navigation**.
 * - Supports **scrolling arrows** when tabs overflow.
 * - Uses React Context for managing state.
 * - Allows custom **tab change handling** via `onChange`.
 *
 * **Migration from the old Tabs component**
 *
 *   - `handler` renamed to `onChange` for handling tab selection updates.
 *   - Removed `mix`, `block`. Use `className` instead.
 *   - Removed direct use of `onMouseEnter`, `onMouseLeave` to avoid overriding native event handlers.
 *   - `onArrowClick` remains for handling scrolling arrows.
 *   - Removed `type` prop. Use dedicated wrappers instead: `SidebarTabs`, `ContentTabs`, `LinksList`.
 *
 * **Variants**
 *
 * - **SidebarTabs** – Tabs with vertical orientation.
 * - **ContentTabs** – Tabs designed for structured content navigation.
 * - **LinksList** – Acts as a navigation list with link-based tabs.
 *
 * **A11y usage notes**
 * - Tabs should have aria-label or aria-labelledby
 *
 * @see [ARIA Tabs Pattern](https://www.w3.org/WAI/ARIA/apg/patterns/tabs/)
 * @see [Storybook](https://storybook.dev.pitchbookdata.com/?path=/docs/components-tabs-tabs--documentation)
 * @see [Changelog](https://gitlab.com/pitchbook/pb-core/platform-fe/ui-kit/-/blob/master/ts-packages/kit/components/tabs/CHANGELOG.md)
 * @see [GitLab](https://gitlab.com/pitchbook/pb-core/platform-fe/ui-kit/-/tree/master/ts-packages/kit/components/tabs)
 */

export const Tabs = forwardRef<HTMLUListElement, TabsProps>(
    function Tabs(props, ref) {
        const {
            children,
            enableArrowNavigation = true,
            ellipsis = true,
            onArrowClick,
            onChange: onChangeProp,
            orientation = 'horizontal',
            enableScroll = orientation !== 'vertical',
            rootProps,
            rootRef,
            value: valueProp = null,
            tabPanelId,
            tabsId: tabsIdProp,
            ...ulListProps
        } = props;

        const defaultTabsIdRef = useRef(uniqId('tabs-'));
        const tabsId = tabsIdProp ?? defaultTabsIdRef.current;

        const [value, onChange] = useControlledState<string>(valueProp || '', onChangeProp);
        const rootElementRef = useRef<HTMLDivElement | null>(null);
        const tabListRef = useRef<HTMLUListElement | null>(null);

        useImperativeHandle(rootRef, () => rootElementRef.current as HTMLDivElement, []);
        useImperativeHandle(ref, () => tabListRef.current as HTMLUListElement, []);

        const {context: arrowNavigationContext, getParentProps} = useArrowNavigation({restoreOnBlur: false});

        const isVertical = orientation === 'vertical';

        const {
            scrollRef,
            containerRef,
            isAtScrollStart,
            isAtScrollEnd,
            updateScrollBoundaries,
        } = useScrollNavigation({
            enabled: enableScroll,
            isVertical,
        });

        useScrollableTabs({enableScroll, contentRef: tabListRef, containerRef: scrollRef});

        const handleScrollArrow = useCallback((isLeftDirection: boolean) => {
            if (!scrollRef.current || !containerRef.current) {
                return;
            }

            const {scrollPosition, viewportSize} = getScrollMetrics(scrollRef.current, isVertical);
            const items = Array.from(containerRef.current.children) as HTMLElement[];

            if (items.length === 0) {
                return;
            }

            const containerRect = containerRef.current.getBoundingClientRect();
            const {itemStartEdges, itemEndEdges} = calculateItemsEdges(items, containerRect, isVertical);

            const hiddenStartIndex = itemStartEdges.findIndex((start: number) => start >= scrollPosition);
            const hiddenEndIndex = itemEndEdges.findIndex((end: number) => end > scrollPosition + viewportSize) - 1;

            const targetIndex = isLeftDirection
                ? Math.max(hiddenStartIndex - 1, 0)
                : Math.min(hiddenEndIndex + 1, itemStartEdges.length - 1);

            const targetScroll = isLeftDirection
                ? itemStartEdges[targetIndex] - OFFSET_TOLERANCE
                : itemEndEdges[targetIndex] - viewportSize + OFFSET_TOLERANCE;

            scrollRef.current.scrollTo({
                [isVertical ? 'top' : 'left']: targetScroll,
                behavior: 'smooth',
            });

            if (isVertical) {
                onArrowClick?.(isLeftDirection ? 'TOP' : 'BOTTOM');
            } else {
                onArrowClick?.(isLeftDirection ? 'LEFT' : 'RIGHT');
            }
        }, [isVertical, onArrowClick, scrollRef, containerRef]);

        const tabListProps: React.HTMLAttributes<HTMLUListElement> = {
            'role': 'tablist',
            'aria-orientation': orientation,
            ...ulListProps,
            'id': tabsId,
            'className': classNames('tabs__list', ulListProps?.className, {
                tabs__list_vertical: isVertical,
            }),
        };

        const memoizedTabsContext = useMemo(() => ({
            enableArrowNavigation,
            ellipsis,
            value,
            onChange,
            isVertical,
            tabPanelId,
            tabsId,
        }), [enableArrowNavigation, value, ellipsis, onChange, isVertical, tabPanelId, tabsId]);

        return (
            <TabsContext.Provider value={memoizedTabsContext}>
                <ArrowNavigationContext.Provider value={arrowNavigationContext}>
                    <div
                        {...rootProps}
                        {...getParentProps()}
                        ref={rootElementRef}
                        className={classNames('tabs', rootProps?.className)}>
                        <SlideArrows
                            className={classes['tabs__slide-arrow']}
                            isVertical={isVertical}
                            isAtScrollStart={isAtScrollStart}
                            isAtScrollEnd={isAtScrollEnd}
                            onClick={handleScrollArrow}
                        />
                        <div
                            ref={scrollRef}
                            className={classNames({tabs__scroll: enableScroll})}
                            onScroll={() => enableScroll && updateScrollBoundaries()}>
                            <ul
                                {...tabListProps}
                                ref={mergeRefs(tabListRef, containerRef as React.Ref<HTMLUListElement>)}>
                                {children}
                            </ul>
                        </div>
                    </div>
                </ArrowNavigationContext.Provider>
            </TabsContext.Provider>
        );
    },
);

Tabs.displayName = 'Tabs';

export const SidebarTabs = (props: TabsProps) => (
    <Tabs
        {...props}
        orientation="vertical"
        rootProps={{className: classNames('tabs_sidebar', 'tabs_content', props?.rootProps?.className)}}
    />
);

SidebarTabs.displayName = 'SidebarTabs';

export const ContentTabs = (props: TabsProps) => (
    <Tabs
        {...props}
        orientation="vertical"
        rootProps={{className: classNames('tabs_content', props?.rootProps?.className)}}
    />
);

ContentTabs.displayName = 'ContentTabs';

export const LinksList = (props: TabsProps) => (
    <Tabs
        aria-orientation={undefined}
        role={undefined}
        enableArrowNavigation={false}
        {...props}
    />
);

LinksList.displayName = 'LinksList';
